import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { Search, Add, FilterList } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  setSearch,
  resetAll,
  setFilter,
} from "../../../../redux/purchaseVoucher/purchaseVoucherSlice";
import { useGetList } from "../../../../hooks/useGetList";
import { XIcon } from "lucide-react";
import debounce from "../../../../Utils/debounce";

const PurchaseVoucherHeader = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const filter = useSelector((state) => state.filterPurchaseVoucher);

  const { data: branchData, loading: branchLoading } =
    useGetList(`branch/options`);
  const { data: supplierData, loading: supplierLoading } =
    useGetList(`supplier/options`);

  const inputRef = useRef();

  const paymentTypeOptions = [
    { value: "", label: "All Payment Types" },
    { value: "Cash", label: "Cash" },
    { value: "Bank", label: "Bank" },
  ];

  const handleSearchChange = debounce(() => {
    if (inputRef.current) {
      dispatch(setSearch(inputRef.current.value));
    }
  }, 500);

  const handleBranchChange = (event) => {
    dispatch(setFilter({ relatedBranch: event.target.value }));
  };

  const handleSupplierChange = (event) => {
    dispatch(setFilter({ relatedSupplier: event.target.value }));
  };

  const handlePaymentTypeChange = (event) => {
    dispatch(setFilter({ paymentType: event.target.value }));
  };

  const [tempStartDate, setTempStartDate] = useState(
    filter?.filterType?.startDate || "",
  );
  const [tempEndDate, setTempEndDate] = useState(
    filter?.filterType?.endDate || "",
  );

  const handleStartDateChange = (event) => {
    setTempStartDate(event.target.value);
  };

  const handleEndDateChange = (event) => {
    setTempEndDate(event.target.value);
  };

  const handleDateFilter = () => {
    dispatch(
      setFilter({
        startDate: tempStartDate,
        endDate: tempEndDate,
      }),
    );
  };

  const handleClearFilters = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    setTempStartDate("");
    setTempEndDate("");
    dispatch(resetAll());
  };

  const handleAddPurchaseVoucher = () => {
    navigate("/add_purchase_voucher");
  };

  const handleClearSearch = () => {
    dispatch(setSearch(""));
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const hasActiveFilters =
    filter?.keyword ||
    filter?.filterType?.relatedBranch ||
    filter?.filterType?.relatedSupplier ||
    filter?.filterType?.paymentType ||
    filter?.filterType?.startDate ||
    filter?.filterType?.endDate;

  return (
    <>
      {/* Main Search and Filters Card */}
      <Card sx={{ mb: 2, boxShadow: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="subtitle2"
            color="primary"
            sx={{ fontWeight: 600, mb: 2 }}
          >
            Search & Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            {/* Search Field */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                inputRef={inputRef}
                placeholder="Search by keyword"
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <Search sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                  endAdornment: inputRef.current?.value ? (
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                      sx={{ ml: 1 }}
                    >
                      <XIcon fontSize="small" size={12} />
                    </IconButton>
                  ) : null,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>

            {/* Supplier Filter */}
            <Grid item xs={12} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Supplier</InputLabel>
                <Select
                  value={filter?.filterType?.relatedSupplier || ""}
                  onChange={handleSupplierChange}
                  label="Supplier"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All Suppliers</MenuItem>
                  {!supplierLoading &&
                    supplierData?.data?.map((supplier) => (
                      <MenuItem key={supplier._id} value={supplier._id}>
                        {supplier.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Branch Filter */}
            <Grid item xs={12} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Branch</InputLabel>
                <Select
                  value={filter?.filterType?.relatedBranch || ""}
                  onChange={handleBranchChange}
                  label="Branch"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All Branches</MenuItem>
                  {!branchLoading &&
                    branchData?.data?.map((branch) => (
                      <MenuItem key={branch._id} value={branch._id}>
                        {branch.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Payment Type Filter */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment Type</InputLabel>
                <Select
                  value={filter?.filterType?.paymentType || ""}
                  onChange={handlePaymentTypeChange}
                  label="Payment Type"
                  sx={{ borderRadius: 2 }}
                >
                  {paymentTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Action Buttons Row */}
          <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Box display="flex" gap={1} justifyContent="flex-end">
                {hasActiveFilters && (
                  <Tooltip title="Clear all filters">
                    <IconButton
                      onClick={handleClearFilters}
                      color="primary"
                      sx={{ borderRadius: 2 }}
                    >
                      <XIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <Box mt={2} display="flex" gap={1} flexWrap="wrap">
              <FilterList sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="caption" color="textSecondary">
                Active filters:
              </Typography>

              {filter?.keyword && (
                <Chip
                  label={`Search: ${filter.keyword}`}
                  size="small"
                  onDelete={handleClearSearch}
                  color="primary"
                  variant="outlined"
                />
              )}

              {filter?.filterType?.relatedSupplier && (
                <Chip
                  label={`Supplier: ${supplierData?.data?.find((s) => s._id === filter.filterType.relatedSupplier)?.name || filter.filterType.relatedSupplier}`}
                  size="small"
                  onDelete={() => dispatch(setFilter({ relatedSupplier: "" }))}
                  color="primary"
                  variant="outlined"
                />
              )}

              {filter?.filterType?.relatedBranch && (
                <Chip
                  label={`Branch: ${branchData?.data?.find((b) => b._id === filter.filterType.relatedBranch)?.name || filter.filterType.relatedBranch}`}
                  size="small"
                  onDelete={() => dispatch(setFilter({ relatedBranch: "" }))}
                  color="primary"
                  variant="outlined"
                />
              )}

              {filter?.filterType?.paymentType && (
                <Chip
                  label={`Payment: ${filter.filterType.paymentType}`}
                  size="small"
                  onDelete={() => dispatch(setFilter({ paymentType: "" }))}
                  color="primary"
                  variant="outlined"
                />
              )}

              {filter?.filterType?.startDate && (
                <Chip
                  label={`Start: ${filter.filterType.startDate}`}
                  size="small"
                  onDelete={() => dispatch(setFilter({ startDate: "" }))}
                  color="primary"
                  variant="outlined"
                />
              )}

              {filter?.filterType?.endDate && (
                <Chip
                  label={`End: ${filter.filterType.endDate}`}
                  size="small"
                  onDelete={() => dispatch(setFilter({ endDate: "" }))}
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Date Filter Card - Separated for Visual Distinction */}
      <Card sx={{ mb: 3, boxShadow: 1 }}>
        <CardContent sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography
              variant="subtitle2"
              color="primary"
              sx={{ fontWeight: 600 }}
            >
              Date Range Filter
            </Typography>
            <Box flexGrow={1} />

            <TextField
              size="small"
              type="date"
              label="Start Date"
              value={tempStartDate}
              onChange={handleStartDateChange}
              InputLabelProps={{ shrink: true }}
              sx={{
                borderRadius: 2,
                minWidth: 150,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />

            <TextField
              size="small"
              type="date"
              label="End Date"
              value={tempEndDate}
              onChange={handleEndDateChange}
              InputLabelProps={{ shrink: true }}
              sx={{
                borderRadius: 2,
                minWidth: 150,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />

            <Button
              variant="outlined"
              size="small"
              onClick={handleDateFilter}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              Apply Filter
            </Button>
          </Box>
        </CardContent>
      </Card>
    </>
  );
};

export default PurchaseVoucherHeader;
